/*
 * pdp-cart.js
 * ---------------------------------------------------------------
 * The cart drawer.
 *
 * Replaces Rebuy Smart Cart, which needed 386KB compressed across 23
 * requests and four API round trips before it could open. This is one
 * class over server-rendered Liquid.
 *
 * Three lessons from the navigation and search drawers are baked in:
 *
 *   - the panel is moved to <body>, so a `transform` or a
 *     `backdrop-filter` on any ancestor cannot become its containing
 *     block and clamp a `position: fixed` panel to the header.
 *   - the page is locked on the root element, never on <body>.
 *     `overflow: hidden` on body makes body a scroll container and the
 *     sticky header stops sticking -- which is what Rebuy did.
 *   - `top` and `height` are measured from `visualViewport`, so the
 *     panel sits over what can actually be seen rather than ending
 *     behind the iOS toolbar.
 * ---------------------------------------------------------------
 */

class CartNoma extends HTMLElement {
  constructor() {
    super();
    this.open = false;
    this.panel = this.querySelector('[data-cnoma-panel]');
    this.overlay = this.querySelector('[data-cnoma-overlay]');
    this.onKeydown = this.onKeydown.bind(this);
    this.measure = this.measure.bind(this);
  }

  connectedCallback() {
    if (!this.panel) return;

    /* Panel and overlay both go to <body>, overlay first so it stays
       behind. A stale pair from a previous render is dropped rather than
       left to accumulate. */
    [this.overlay, this.panel].forEach((el) => {
      if (!el || el.parentElement === document.body) return;
      const attr = el === this.panel ? 'data-cnoma-panel' : 'data-cnoma-overlay';
      const stale = document.body.querySelector(`:scope > [${attr}]`);
      if (stale && stale !== el) stale.remove();
      document.body.appendChild(el);
    });

    this.swap = this.panel.querySelector('[data-cnoma-swap]');
    this.sheet = this.panel.querySelector('[data-cnoma-sheet]');
    this.pickerCache = new Map();

    this.panel.querySelector('[data-cnoma-close]').addEventListener('click', () => this.setOpen(false, true));
    if (this.overlay) this.overlay.addEventListener('click', () => this.setOpen(false));

    /* One delegated listener on the panel rather than a listener per
       button: the rows are replaced wholesale on every change, and
       per-element listeners would have to be rebound each time. */
    this.panel.addEventListener('click', (event) => this.onPanelClick(event));

    this.bindTriggers();
    this.bindAddToCart();

    window.addEventListener('resize', this.measure);
    /* The mobile toolbar slides in and out without firing `resize`. */
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.measure);
      window.visualViewport.addEventListener('scroll', this.measure);
    }
    this.measure();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.measure);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.measure);
      window.visualViewport.removeEventListener('scroll', this.measure);
    }
    document.removeEventListener('keydown', this.onKeydown);
    this.unlock();
  }

  /* Anything that should open the cart instead of navigating to /cart.
   *
   * The links keep their `href`, so without JavaScript -- or before this
   * file has parsed -- they still go to the cart page. The drawer is an
   * enhancement over a working link, not a replacement for one. */
  bindTriggers() {
    if (this.triggersBound) return;
    this.triggersBound = true;

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('#cart-icon-bubble, [data-cnoma-open]');
      if (!trigger) return;
      /* Leave modified clicks alone: cmd-click, middle-click and
         "open in new tab" should still reach the cart page. */
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
      event.preventDefault();
      this.setOpen(true);
    });
  }

  /* Every add-to-cart on the store, caught in one place.
   *
   * There are at least four separate handlers posting to `/cart/add`:
   * Dawn's `product-form`, two sticky bars, and the cross-sell snippet.
   * Three of them bind `submit` on their own form and do their own fetch,
   * and one of those follows it with a second call to `/cart.js` it does
   * not need.
   *
   * Rather than edit four files -- and find the fifth later -- this
   * listens on `document` in the CAPTURE phase, which runs before any
   * listener bound to the form itself. `stopImmediatePropagation` there
   * means the event never reaches them. Their code is untouched and still
   * works if this file is removed: the forms are ordinary forms with a
   * real `action`, so with no JavaScript at all they post to the cart
   * page as they always did.
   *
   * A form marked `data-cnoma-ignore` is left alone, as a way out if some
   * future form needs its own behaviour. */
  bindAddToCart() {
    if (this.addBound) return;
    this.addBound = true;

    /* The product card's quick-add is the one path that is not a form
       submit -- `pdp-grid.js` listens for a click on `[data-pgrid-add]`
       and posts on its own. Caught here in the capture phase for the same
       reason and in the same way, so a card on a collection page, in
       search results, or in this drawer's own empty state all open the
       cart like everything else. `pdp-grid.js` is left as it is; its
       listener simply never fires. */
    document.addEventListener(
      'click',
      (event) => {
        /* `.cs-add-btn` is the quick add in "Complete your setup". It came
           with its own `fetch` that added the item and turned the button
           into a tick, and never told the cart -- so the drawer stayed
           shut, the header count stayed wrong, and the only sign anything
           had happened was a tick on a button forty pixels below the eye.

           Routed through here it behaves like every other add on the site:
           the drawer opens on the click and the item lands in it. Being a
           capture-phase listener that stops immediate propagation, the
           snippet's own handler never runs, so there is no double add.

           `[data-explore-add]` is here for the same reason. Explore more
           was adding the item correctly -- the cart went up, the request
           came back 200 -- and showing nothing for it: the drawer stayed
           shut, and the header bubble it tried to update does not exist
           while the cart is empty, so the first add of a session looked
           like a dead button.

           `[data-proof-add]` is the "+" on the product strip under each
           customer video, when that section is set to add rather than
           link. It had no handler anywhere -- the homepage uses the link
           form, so nothing had ever pressed it -- and on the product page,
           where it is the right behaviour, a real click added nothing. */
        const button =
          event.target.closest &&
          event.target.closest('[data-pgrid-add], .cs-add-btn, [data-nd-add], [data-explore-add], [data-proof-add]');
        if (!button || button.hasAttribute('data-cnoma-ignore')) return;
        if (button.disabled || button.classList.contains('is-busy')) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        /* Complete the setup and the bundle row keep their ids in
           `data-nd-add` -- one of them, or two on the "add both" row. */
        this.add(button.getAttribute('data-variant-id') || button.getAttribute('data-nd-add'), button);
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target.closest && event.target.closest('form[action*="/cart/add"]');
        if (!form || form.hasAttribute('data-cnoma-ignore')) return;

        /* Respect a button that has already been marked unavailable --
           Dawn sets `aria-disabled` on a sold-out variant, and clicking
           it should do nothing rather than post. */
        const button = form.querySelector('[type="submit"]');
        if (button && (button.disabled || button.getAttribute('aria-disabled') === 'true')) {
          event.preventDefault();
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        this.submitForm(form, button);
      },
      true
    );
  }

  /* Open first, then load.
   *
   * The round trip to `/cart/add.js` is around 700ms because Shopify is
   * rendering the drawer's HTML as part of it. Waiting for that before
   * showing anything makes the button feel broken and is where a lot of
   * add-to-carts get clicked twice. The drawer opens on the tap instead,
   * still showing the cart as it was, with a progress line under the
   * heading; the new item drops in when the response lands. */
  /*
   * If the same pack-priced product is added again, Shopify must not keep
   * two independent 1-pack lines. Example: 1 DeskMate + another 1 DeskMate
   * should become the real 2-pack variant, with its 2 free gifts.
   *
   * This is intentionally generic: the tier ids and gift ids come from the
   * current Product Info markup's data-nd-* attributes. Nothing here is
   * hard-coded to DeskMate or to numeric Shopify ids.
   *
   * Returns true when the new add was consumed by a merge. Returns false when
   * the normal add flow should continue (no existing pack, or the combined
   * quantity is above the largest defined tier).
   */
  async mergeExistingPackBundle(body, bundleMap, bundleKey, ticked) {
    const incomingQty = Math.max(1, Number(body.get('quantity')) || 1);
    const tiers = Object.keys(bundleMap || {})
      .map((value) => Number(value))
      .filter((value) => value > 0)
      .sort((a, b) => a - b);

    if (!tiers.length) return false;

    const maxTier = tiers[tiers.length - 1];
    const bundleVariantIds = new Set(
      tiers
        .map((qty) => Number(bundleMap[String(qty)] && bundleMap[String(qty)].id) || 0)
        .filter((id) => id > 0)
    );

    if (!bundleVariantIds.size) return false;

    const cartResponse = await fetch(`${window.routes.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!cartResponse.ok) {
      throw new Error('Could not read the cart before combining the bundle.');
    }

    const cart = await cartResponse.json();

    /* Main bundle lines are recognised by their variant id. Gift/add-on lines
       are explicitly excluded because they share the bundle key but are not
       part of the desk/unit count. This also picks up duplicate groups already
       created by the previous add behaviour. */
    const existingMainItems = (cart.items || []).filter((cartItem) => {
      const props = cartItem.properties || {};
      return (
        bundleVariantIds.has(Number(cartItem.id)) &&
        props._nd_bundle_gift !== '1' &&
        props._nd_bundle_addon !== '1'
      );
    });

    if (!existingMainItems.length) return false;

    const existingQty = existingMainItems.reduce(
      (sum, cartItem) => sum + Math.max(0, Number(cartItem.quantity) || 0),
      0
    );

    const targetQty = existingQty + incomingQty;

    /* There is no safe price tier beyond the largest configured pack.
       Leave the normal add flow alone in that case; Shopify may keep e.g.
       a 3-pack plus a separate 1-pack rather than inventing a 4-pack price. */
    if (targetQty > maxTier || !bundleMap[String(targetQty)]) {
      return false;
    }

    const target = bundleMap[String(targetQty)];
    if (!target || Number(target.id) < 1) {
      throw new Error('The combined bundle tier is missing.');
    }

    const groupKeys = new Set();
    existingMainItems.forEach((cartItem) => {
      const props = cartItem.properties || {};
      if (props._nd_bundle_key) groupKeys.add(String(props._nd_bundle_key));
    });

    /* Everything belonging to the old matching groups must be replaced
       together: main product, free gifts and paid add-ons. Legacy main lines
       without a group key are still removed by their own line key. */
    const oldItemsByKey = new Map();

    existingMainItems.forEach((cartItem) => {
      oldItemsByKey.set(cartItem.key, cartItem);
    });

    (cart.items || []).forEach((cartItem) => {
      const props = cartItem.properties || {};
      if (props._nd_bundle_key && groupKeys.has(String(props._nd_bundle_key))) {
        oldItemsByKey.set(cartItem.key, cartItem);
      }
    });

    const oldGroupItems = Array.from(oldItemsByKey.values());

    /* Preserve paid add-ons already in the cart, and add any currently ticked
       add-ons only for the newly-added desks. Quantities are additive here so
       adding a second DeskMate without an add-on never silently charges for a
       second accessory. */
    const addonCounts = new Map();

    const addAddon = (variantId, quantity, productId, properties) => {
      const id = Number(variantId) || 0;
      const qty = Number(quantity) || 0;
      if (id < 1 || qty < 1) return;

      const key = String(id);
      const current = addonCounts.get(key) || {
        id,
        quantity: 0,
        productId: Number(productId) || 0,
        properties: Object.assign({}, properties || {}),
      };

      current.quantity += qty;
      if (!current.productId && productId) current.productId = Number(productId) || 0;
      addonCounts.set(key, current);
    };

    oldGroupItems.forEach((cartItem) => {
      const props = cartItem.properties || {};
      if (props._nd_bundle_addon === '1') {
        addAddon(
          cartItem.id,
          cartItem.quantity,
          props._nd_bundle_addon_product,
          props
        );
      }
    });

    Array.from(ticked || []).forEach((box) => {
      addAddon(
        box.value,
        incomingQty,
        box.getAttribute('data-product') || 0,
        {
          _nd_bundle_addon: '1',
          _nd_bundle_addon_product: box.getAttribute('data-product') || '',
        }
      );
    });

    const targetGiftProducts = new Set(
      (target.giftProducts || [])
        .map((id) => Number(id))
        .filter((id) => id > 0)
    );

    /* Add the replacement group first. If Shopify rejects it, the customer's
       original cart remains untouched. A fresh key also prevents Shopify from
       coalescing the replacement with one of the groups we are about to remove. */
    const newBundleKey =
      bundleKey ||
      (
        window.crypto && typeof window.crypto.randomUUID === 'function'
          ? window.crypto.randomUUID()
          : `nd-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
      );

    const firstMainProps = Object.assign(
      {},
      (existingMainItems[0] && existingMainItems[0].properties) || {}
    );

    delete firstMainProps._nd_bundle_gift;
    delete firstMainProps._nd_bundle_addon;
    delete firstMainProps._nd_bundle_addon_product;

    const mainProperties = Object.assign(firstMainProps, {
      _nd_bundle_key: newBundleKey,
      _nd_bundle_map: JSON.stringify(bundleMap),
      _nd_bundle_qty: String(targetQty),
    });

    const replacementItems = [
      {
        id: Number(target.id),
        quantity: targetQty,
        properties: mainProperties,
      },
    ];

    addonCounts.forEach((addon) => {
      /* If the target tier gives this product away for free, do not carry a
         paid version of the same add-on into the replacement group. */
      if (addon.productId && targetGiftProducts.has(addon.productId)) return;

      const addonProperties = Object.assign({}, addon.properties || {}, {
        _nd_bundle_key: newBundleKey,
        _nd_bundle_addon: '1',
      });

      delete addonProperties._nd_bundle_gift;

      if (addon.productId) {
        addonProperties._nd_bundle_addon_product = String(addon.productId);
      }

      replacementItems.push({
        id: addon.id,
        quantity: addon.quantity,
        properties: addonProperties,
      });
    });

    (target.gifts || []).forEach((giftId) => {
      const id = Number(giftId) || 0;
      if (id < 1) return;

      replacementItems.push({
        id,
        quantity: targetQty,
        properties: {
          _nd_bundle_key: newBundleKey,
          _nd_bundle_gift: '1',
        },
      });
    });

    const addResponse = await fetch(`${window.routes.cart_add_url || '/cart/add'}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items: replacementItems }),
    });

    const added = await addResponse.json();

    if (!addResponse.ok || added.status) {
      throw new Error(
        added.description ||
          added.message ||
          'Could not combine the product into the correct bundle tier.'
      );
    }

    const updates = {};
    oldGroupItems.forEach((cartItem) => {
      updates[cartItem.key] = 0;
    });

    const removeResponse = await fetch(`${window.routes.cart_update_url || '/cart/update'}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        updates,
        sections: 'cart-noma',
        sections_url: window.location.pathname,
      }),
    });

    const finalCart = await removeResponse.json();

    if (!removeResponse.ok || finalCart.errors) {
      /* Best-effort rollback: remove only the replacement group we just added.
         This avoids leaving both the old bundle and the replacement in cart if
         the second request fails. */
      try {
        const rollbackCart = await fetch(`${window.routes.cart_url || '/cart'}.js`, {
          headers: { Accept: 'application/json' },
        }).then((response) => response.json());

        const rollbackUpdates = {};

        (rollbackCart.items || []).forEach((cartItem) => {
          const props = cartItem.properties || {};
          if (String(props._nd_bundle_key || '') === String(newBundleKey)) {
            rollbackUpdates[cartItem.key] = 0;
          }
        });

        if (Object.keys(rollbackUpdates).length) {
          await fetch(`${window.routes.cart_update_url || '/cart/update'}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ updates: rollbackUpdates }),
          });
        }
      } catch (rollbackError) {
        console.error('Could not roll back the combined bundle:', rollbackError);
      }

      throw new Error(
        finalCart.description ||
          finalCart.message ||
          'Could not finish combining the bundle.'
      );
    }

    this.render(finalCart);
    this.setCount(finalCart.item_count);
    return true;
  }

  async submitForm(form, button) {
    if (this.busy) return;
    this.busy = true;

    this.clearError();
    this.setOpen(true);
    this.panel.classList.add('is-pending');
    if (button) button.classList.add('cnoma-submitting');

    try {
      const body = new FormData(form);

      /* Whatever is ticked in "Complete the setup" goes up with the
         product, in one request.
         
         /cart/add.js takes either a single id or an `items` array, and
         they cannot be mixed -- so the moment an add-on is ticked the
         product moves into the array too and its own `id`/`quantity`
         come out. One post means one cart render and no chance of the
         accessory landing while the product fails. */
      /* Colour or size bundles: one variant id per item, written by
         nd-product.js. Grouped into one line per variant, and ticked
         add-ons follow at the bundle's full quantity. */
      const unitsField = form.querySelector('[data-nd-units]') || document.querySelector('[data-nd-units]');
      const unitIds = unitsField && unitsField.value ? unitsField.value.split(',').filter(Boolean) : [];

      /* Free gifts belong to the selected bundle tier, but are real $0 Shopify
         products so checkout remains the source of truth. The Liquid puts the
         selected gift variant ids on the checked tier; no product/variant id is
         hard-coded here. Quantity follows the bundle (2 desks => 2 gifts, etc.).

         Pack-priced products need one more piece of information in the cart:
         which variant belongs to quantity 1 / 2 / 3. Without that, Shopify lets
         a customer change a 2-pack line from quantity 2 to quantity 1 while
         keeping the cheaper "2 DeskMate Pro" variant price. We store a private
         bundle map on the main line and a private group key on every related
         gift/add-on. The cart drawer can then change tier, price and gifts as
         one unit instead of changing quantity alone. */
      const productRoot = form.closest('[data-nd-product]') || document.querySelector('[data-nd-product]');
      const selectedTier = productRoot && productRoot.querySelector('[data-nd-tier]:checked');
      const tierInputs = productRoot
        ? Array.from(productRoot.querySelectorAll('[data-nd-tier]'))
        : [];

      const freeGifts = [];
      if (selectedTier) {
        const giftQty = Math.max(
          1,
          Number(selectedTier.getAttribute('data-nd-cart-gift-qty')) ||
            Number(body.get('quantity')) ||
            1
        );

        ['1', '2'].forEach((slot) => {
          const giftId = Number(selectedTier.getAttribute(`data-nd-cart-gift-${slot}`));
          if (giftId > 0) freeGifts.push({ id: giftId, quantity: giftQty });
        });
      }

      const bundleMap = {};
      const bundleVariantIds = new Set();

      tierInputs.forEach((tier) => {
        const qty = Number(tier.getAttribute('data-nd-quantity-value')) || 0;
        const variantId =
          Number(tier.getAttribute('data-nd-variant-value')) ||
          Number(tier.value) ||
          0;

        if (qty < 1 || variantId < 1) return;

        const gifts = ['1', '2']
          .map((slot) => Number(tier.getAttribute(`data-nd-cart-gift-${slot}`)) || 0)
          .filter((id) => id > 0);

        const giftProducts = [
          Number(tier.getAttribute('data-nd-gift-product')) || 0,
          Number(tier.getAttribute('data-nd-gift-product-2')) || 0,
        ].filter((id) => id > 0);

        bundleMap[String(qty)] = {
          id: variantId,
          gifts,
          giftProducts,
        };

        bundleVariantIds.add(variantId);
      });

      /* "Options" products also have quantity rows, but all rows point to the
         same colour/size variant. A real pack has a different Shopify variant
         for 1, 2 and 3, so only those products get protected bundle behaviour. */
      const isPackBundle =
        !!selectedTier &&
        bundleVariantIds.size > 1 &&
        Object.keys(bundleMap).length > 1;

      const bundleKey = isPackBundle
        ? (
            window.crypto && typeof window.crypto.randomUUID === 'function'
              ? window.crypto.randomUUID()
              : `nd-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
          )
        : '';

      const bundleMapJson = isPackBundle ? JSON.stringify(bundleMap) : '';

      const ticked = productRoot
        ? productRoot.querySelectorAll('[data-nd-addon]:checked')
        : document.querySelectorAll('[data-nd-addon]:checked');

      /* Repeated pack adds are one purchase quantity, not independent bundle
         groups. Example: add 1 DeskMate, then add 1 DeskMate again => switch
         the existing cart to the real 2-pack variant and grant tier-2 gifts. */
      if (isPackBundle && !unitIds.length && body.get('id')) {
        const merged = await this.mergeExistingPackBundle(
          body,
          bundleMap,
          bundleKey,
          ticked
        );

        if (merged) return;
      }

      if (unitIds.length && body.get('id')) {
        const counts = {};
        unitIds.forEach((id) => { counts[id] = (counts[id] || 0) + 1; });
        body.delete('id');
        body.delete('quantity');
        let n = 0;
        Object.keys(counts).forEach((id) => {
          body.append(`items[${n}][id]`, id);
          body.append(`items[${n}][quantity]`, String(counts[id]));
          n++;
        });
        document.querySelectorAll('[data-nd-addon]:checked').forEach((box) => {
          body.append(`items[${n}][id]`, box.value);
          body.append(`items[${n}][quantity]`, String(unitIds.length));

          if (isPackBundle) {
            body.append(`items[${n}][properties][_nd_bundle_key]`, bundleKey);
            body.append(`items[${n}][properties][_nd_bundle_addon]`, '1');
            body.append(
              `items[${n}][properties][_nd_bundle_addon_product]`,
              box.getAttribute('data-product') || ''
            );
          }

          n++;
        });

        freeGifts.forEach((gift) => {
          body.append(`items[${n}][id]`, String(gift.id));
          body.append(`items[${n}][quantity]`, String(gift.quantity));

          if (isPackBundle) {
            body.append(`items[${n}][properties][_nd_bundle_key]`, bundleKey);
            body.append(`items[${n}][properties][_nd_bundle_gift]`, '1');
          }

          n++;
        });
      }

      if (!unitIds.length && (ticked.length || freeGifts.length || isPackBundle) && body.get('id')) {
        const mainId = body.get('id');
        const mainQty = body.get('quantity') || '1';
        body.delete('id');
        body.delete('quantity');
        body.append('items[0][id]', mainId);
        body.append('items[0][quantity]', mainQty);

        if (isPackBundle) {
          body.append('items[0][properties][_nd_bundle_key]', bundleKey);
          body.append('items[0][properties][_nd_bundle_map]', bundleMapJson);
          body.append('items[0][properties][_nd_bundle_qty]', String(mainQty));
        }

        /* One paid add-on per desk, matching the pack -- the same number the
           card priced and the button totalled. */
        let n = 1;
        ticked.forEach((box) => {
          body.append(`items[${n}][id]`, box.value);
          body.append(`items[${n}][quantity]`, mainQty);

          if (isPackBundle) {
            body.append(`items[${n}][properties][_nd_bundle_key]`, bundleKey);
            body.append(`items[${n}][properties][_nd_bundle_addon]`, '1');
            body.append(
              `items[${n}][properties][_nd_bundle_addon_product]`,
              box.getAttribute('data-product') || ''
            );
          }

          n++;
        });

        freeGifts.forEach((gift) => {
          body.append(`items[${n}][id]`, String(gift.id));
          body.append(`items[${n}][quantity]`, String(gift.quantity));

          if (isPackBundle) {
            body.append(`items[${n}][properties][_nd_bundle_key]`, bundleKey);
            body.append(`items[${n}][properties][_nd_bundle_gift]`, '1');
          }

          n++;
        });
      }

      body.append('sections', 'cart-noma');
      body.append('sections_url', window.location.pathname);

      const response = await fetch(`${window.routes.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });

      const data = await response.json();

      /* Shopify answers 422 with a `description` for the reasons an add
         can legitimately fail -- sold out, or more than the stock on
         hand. That belongs in front of the customer, not in the console. */
      if (!response.ok || data.status) {
        this.showError(data.description || data.message || 'That could not be added to your cart.');
        return;
      }

      this.render(data);
      const chip = this.panel.querySelector('.cnoma__count');
      this.setCount(chip ? Number(chip.textContent.trim()) : null);
    } catch (error) {
      console.error('Cart drawer could not add the item:', error);
      this.showError('Something went wrong. Please try again.');
    } finally {
      this.busy = false;
      this.panel.classList.remove('is-pending');
      if (button) button.classList.remove('cnoma-submitting');
      /* Only now. `loadCrossSell` refuses to run while `is-pending` is
         set -- so the call `render` made a moment ago did nothing, and
         without this the strip stayed empty until some later change
         happened to redraw it. */
      if (this.open) this.loadCrossSell();
    }
  }

  showError(message) {
    let box = this.panel.querySelector('[data-cnoma-error]');
    if (!box) {
      box = document.createElement('p');
      box.className = 'cnoma__error';
      box.setAttribute('data-cnoma-error', '');
      box.setAttribute('role', 'alert');
      this.panel.querySelector('.cnoma__head').insertAdjacentElement('afterend', box);
    }
    box.textContent = message;
  }

  clearError() {
    const box = this.panel.querySelector('[data-cnoma-error]');
    if (box) box.remove();
  }

  /* Pin the panel to what is actually visible.
   *
   * `visualViewport.offsetTop` is how far the layout viewport has been
   * scrolled behind the visual one, and `.height` is what is left after
   * the toolbar and the keyboard have taken their share. Together they
   * place the panel over the visible area exactly. */
  measure() {
    // Height is no longer taken from the visual viewport.
    //
    // vv.height is what is left AFTER the toolbar and the keyboard have
    // taken their share, so the panel shrank whenever either appeared --
    // opening the keyboard in search halved the drawer, and the cart lost
    // height whenever the address bar expanded. The CSS uses 100svh
    // instead: the small viewport is a stable number that already excludes
    // the browser UI, so the panel is full height and stays there.
    //
    // offsetTop is still tracked. That one is not about size: it is how far
    // the layout viewport has been scrolled behind the visual one, and
    // without it a fixed panel drifts off the top of the screen.
    const vv = window.visualViewport;
    const top = vv ? Math.max(0, Math.round(vv.offsetTop)) : 0;
    const root = document.documentElement.style;
    root.setProperty('--cnoma-top', `${top}px`);
    root.removeProperty('--cnoma-h');
  }

  setOpen(next, fromKeyboard) {
    if (next === this.open) return;
    this.open = next;

    this.panel.classList.toggle('is-open', next);
    if (this.overlay) {
      this.overlay.hidden = !next;
      requestAnimationFrame(() => this.overlay.classList.toggle('is-open', next));
    }
    document.documentElement.classList.toggle('cnoma-open', next);

    if (next) {
      /* Measured on every open: the toolbar may have collapsed since
         the last time. */
      this.measure();
      this.lock();
      document.addEventListener('keydown', this.onKeydown);
      /* Focus the panel itself, not a control inside it. Focusing a
         button would draw a focus ring on it the moment the drawer
         opens, which reads as an error state. */
      this.panel.focus({ preventScroll: true });
      this.loadCrossSell();
    } else {
      this.unlock();
      document.removeEventListener('keydown', this.onKeydown);
      if (fromKeyboard) {
        const icon = document.getElementById('cart-icon-bubble');
        if (icon) icon.focus();
      }
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      /* The picker sits over the drawer, so it is what Escape means
         while it is open. */
      if (this.pickerOpen) this.closePicker();
      else this.setOpen(false, true);
      return;
    }

    if (event.key === 'Tab') this.trapFocus(event);
  }

  /* `aria-modal="true"` is a promise that the rest of the page is not
   * reachable. Without this it was a lie: 182 elements outside the panel
   * were still tabbable, so a keyboard user tabbed straight out of the
   * dialog into the page behind it.
   *
   * A wrapping tab rather than `inert` on <body>: `inert` would have to
   * be applied to every sibling of the panel and taken off again, and the
   * panel is portalled to <body> alongside the header, the overlay and
   * whatever an app has appended. Wrapping needs no bookkeeping. */
  trapFocus(event) {
    /* While the picker is up it is the dialog, so the trap is its. */
    const scope = this.pickerOpen && this.sheet ? this.sheet : this.panel;
    const focusable = scope.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    const reachable = Array.from(focusable).filter((el) => {
      /* Offset parent is null for anything `display: none`, and the
         hidden cross-sell slot is full of links until it loads. */
      return el.offsetParent !== null || el === document.activeElement;
    });

    if (!reachable.length) {
      event.preventDefault();
      this.panel.focus({ preventScroll: true });
      return;
    }

    const first = reachable[0];
    const last = reachable[reachable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === scope)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    } else if (!scope.contains(active)) {
      /* Focus escaped some other way -- bring it back. */
      event.preventDefault();
      first.focus();
    }
  }

  onPanelClick(event) {
    /* Choosing an option, without leaving the cart. */
    const choose = event.target.closest('[data-cnoma-choose]');
    if (choose) {
      this.openPicker(choose.getAttribute('data-cnoma-choose'), choose);
      return;
    }

    if (event.target.closest('[data-cnoma-picker-close]')) {
      this.closePicker();
      return;
    }

    /* The product card's own "Choose options" is a link to the product
       page. On a collection page that is right; inside the cart it is
       the one thing the drawer exists to avoid, so here it opens the
       picker instead. The card is untouched -- this only ever fires for
       one rendered inside the panel. */
    const chooseCard = event.target.closest('.pgrid__cta--choose');
    if (chooseCard) {
      const href = chooseCard.getAttribute('href') || '';
      const handle = href.split('/products/')[1];
      if (handle) {
        event.preventDefault();
        this.openPicker(handle.split(/[?#]/)[0], chooseCard);
        return;
      }
    }

    /* Quick add from the empty-state recommendations. Same one-request
       shape as a quantity change: `/cart/add.js` takes a `sections` key
       too, so the drawer comes back filled in the same round trip. */
    const add = event.target.closest('[data-cnoma-add]');
    if (add && add.tagName === 'BUTTON') {
      this.add(add.getAttribute('data-cnoma-add'), add);
      return;
    }

    const remove = event.target.closest('[data-cnoma-remove]');
    const minus = event.target.closest('[data-cnoma-minus]');
    const plus = event.target.closest('[data-cnoma-plus]');
    if (!remove && !minus && !plus) return;

    const row = event.target.closest('[data-cnoma-line]');
    if (!row) return;

    const line = Number(row.getAttribute('data-cnoma-line'));
    const current = Number(row.querySelector('.cnoma__qtynum').textContent.trim()) || 1;

    let quantity = current;
    if (remove) quantity = 0;
    if (minus) quantity = current - 1;
    if (plus) quantity = current + 1;
    if (quantity === current) return;

    this.change(line, Math.max(0, quantity), row);
  }

  /* One request does the whole job.
   *
   * `/cart/change.js` accepts a `sections` key and returns the rendered
   * HTML for those sections alongside the new cart. That is the whole
   * reason `sections/cart-noma.liquid` exists. The alternative -- change,
   * then fetch `/cart.js`, then rebuild the rows in JavaScript -- is two
   * round trips and a second description of a cart row that has to be
   * kept in step with the Liquid one. */
  async change(line, quantity, row) {
    if (this.busy) return;
    this.busy = true;
    if (row) row.classList.add('is-busy');
    this.panel.classList.add('is-loading');

    try {
      /*
       * Quantity alone is NOT a valid edit for a pack-priced line.
       *
       * Example:
       *   "2 DeskMate Pro" is the cheaper two-pack variant at $145 each.
       *   If Shopify simply changes its quantity from 2 to 1, the variant
       *   stays "2 DeskMate Pro" and the customer incorrectly keeps the
       *   two-pack unit price. The free gift is also a separate cart line,
       *   so it would otherwise stay at quantity 2.
       *
       * Bundle lines added by this file carry private _nd_bundle_* properties.
       * Read the current cart once, detect those lines, and switch the whole
       * bundle to the correct 1/2/3 variant instead of changing only quantity.
       */
      const cartResponse = await fetch(`${window.routes.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      });

      const cart = cartResponse.ok ? await cartResponse.json() : null;
      const item = cart && cart.items ? cart.items[line - 1] : null;
      const props = item && item.properties ? item.properties : {};
      const bundleKey = props && props._nd_bundle_key ? String(props._nd_bundle_key) : '';

      /* A free gift may be reduced/removed by the customer, but it can never
         be increased beyond the bundle entitlement from the cart controls. */
      if (item && props && props._nd_bundle_gift === '1') {
        if (quantity > item.quantity) {
          this.showError('Free gift quantity follows the selected bundle quantity.');
          return;
        }

        const response = await fetch(`${window.routes.cart_change_url || '/cart/change'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line,
            quantity,
            sections: 'cart-noma',
            sections_url: window.location.pathname,
          }),
        });

        const data = await response.json();
        this.render(data);
        this.setCount(data.item_count);
        return;
      }

      if (item && bundleKey && props._nd_bundle_map) {
        let bundleMap = {};

        try {
          bundleMap =
            typeof props._nd_bundle_map === 'string'
              ? JSON.parse(props._nd_bundle_map)
              : props._nd_bundle_map;
        } catch (error) {
          console.warn('Could not read bundle map; falling back to normal cart quantity.', error);
        }

        const tiers = Object.keys(bundleMap)
          .map((value) => Number(value))
          .filter((value) => value > 0)
          .sort((a, b) => a - b);

        if (tiers.length) {
          const minTier = tiers[0];
          const maxTier = tiers[tiers.length - 1];

          /* Plus on the largest available tier must not create "4 of the
             3-pack variant" at the 3-pack unit price. Keep the supported max. */
          if (quantity > maxTier) {
            this.showError(`This bundle is available up to ${maxTier}.`);
            return;
          }

          /* Quantity 0 means remove the bundle and every free gift/add-on
             that was added with it. */
          if (quantity <= 0) {
            const updates = {};
            cart.items.forEach((cartItem) => {
              const p = cartItem.properties || {};
              if (String(p._nd_bundle_key || '') === bundleKey) {
                updates[cartItem.key] = 0;
              }
            });

            const response = await fetch(`${window.routes.cart_update_url || '/cart/update'}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                updates,
                sections: 'cart-noma',
                sections_url: window.location.pathname,
              }),
            });

            const data = await response.json();
            this.render(data);
            this.setCount(data.item_count);
            return;
          }

          /* Our pack tiers are discrete: 1, 2 and 3. If something somehow
             asks for a missing quantity, choose the nearest defined tier
             rather than leaving a discounted variant at the wrong quantity. */
          let targetQty = quantity;
          if (!bundleMap[String(targetQty)]) {
            targetQty = tiers.reduce((best, value) => {
              return Math.abs(value - quantity) < Math.abs(best - quantity) ? value : best;
            }, minTier);
          }

          const target = bundleMap[String(targetQty)];
          if (!target || !Number(target.id)) {
            throw new Error('Bundle target variant is missing.');
          }

          const groupItems = cart.items.filter((cartItem) => {
            const p = cartItem.properties || {};
            return String(p._nd_bundle_key || '') === bundleKey;
          });

          const targetGiftProducts = (target.giftProducts || [])
            .map((id) => Number(id))
            .filter((id) => id > 0);

          /* Preserve paid add-ons, but resize them with the new bundle.
             If an add-on becomes free in the target tier, do not re-add its
             paid line -- the free gift line below replaces it. */
          const addons = groupItems
            .filter((cartItem) => {
              const p = cartItem.properties || {};
              return p._nd_bundle_addon === '1';
            })
            .filter((cartItem) => {
              const p = cartItem.properties || {};
              const productId = Number(p._nd_bundle_addon_product) || 0;
              return !productId || !targetGiftProducts.includes(productId);
            });

          const updates = {};
          groupItems.forEach((cartItem) => {
            updates[cartItem.key] = 0;
          });

          /* Remove the old tier/gifts/add-ons first. No section render here:
             the second request below returns the final drawer in one render. */
          const removeResponse = await fetch(`${window.routes.cart_update_url || '/cart/update'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates }),
          });

          if (!removeResponse.ok) {
            throw new Error('Could not remove the previous bundle tier.');
          }

          const mainProperties = Object.assign({}, props, {
            _nd_bundle_qty: String(targetQty),
          });

          const items = [
            {
              id: Number(target.id),
              quantity: targetQty,
              properties: mainProperties,
            },
          ];

          addons.forEach((addon) => {
            const addonProps = Object.assign({}, addon.properties || {}, {
              _nd_bundle_key: bundleKey,
              _nd_bundle_addon: '1',
            });

            items.push({
              id: Number(addon.id),
              quantity: targetQty,
              properties: addonProps,
            });
          });

          (target.gifts || []).forEach((giftId) => {
            const id = Number(giftId);
            if (id < 1) return;

            items.push({
              id,
              quantity: targetQty,
              properties: {
                _nd_bundle_key: bundleKey,
                _nd_bundle_gift: '1',
              },
            });
          });

          const addResponse = await fetch(`${window.routes.cart_add_url || '/cart/add'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items,
              sections: 'cart-noma',
              sections_url: window.location.pathname,
            }),
          });

          const data = await addResponse.json();

          if (!addResponse.ok || data.status) {
            throw new Error(data.description || data.message || 'Could not update the bundle.');
          }

          this.render(data);

          /* /cart/add.js returns the added lines plus rendered sections rather
             than a full cart object, so read the count from the rendered chip. */
          const chip = this.panel.querySelector('.cnoma__count');
          this.setCount(chip ? Number(chip.textContent.trim()) : null);
          return;
        }
      }

      /* Normal products keep the original one-request behaviour. */
      const response = await fetch(`${window.routes.cart_change_url || '/cart/change'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line,
          quantity,
          sections: 'cart-noma',
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();
      this.render(data);
      this.setCount(data.item_count);
    } catch (error) {
      console.error('Cart drawer could not update the line:', error);
      this.showError('The bundle could not be updated. Please try again.');
      if (row) row.classList.remove('is-busy');
    } finally {
      this.busy = false;
      this.panel.classList.remove('is-loading');
    }
  }

  /* `variantId` may name more than one variant, comma separated: the
     "add both" row adds two in a single post so the drawer opens once and
     the cart is rendered once. */
  async add(variantId, button) {
    if (this.busy || !variantId) return;

    const items = String(variantId)
      .split(',')
      .map((id) => Number(id.trim()))
      .filter((id) => id > 0)
      .map((id) => ({ id, quantity: 1 }));

    if (!items.length) return;
    this.busy = true;

    if (button) {
      button.classList.add('is-busy');
      button.disabled = true;
    }

    /* Open on the click, not on the reply -- the same reason the form
       path does. The drawer sliding in is the confirmation; the item
       lands in it a moment later. */
    this.clearError();
    this.setOpen(true);
    this.panel.classList.add('is-pending');

    try {
      const response = await fetch(`${window.routes.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          sections: 'cart-noma',
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        this.showError(data.description || data.message || 'That could not be added to your cart.');
        return;
      }

      /* Whatever was chosen is now in the cart, so the sheet has done
         its job and the cart underneath is the thing to look at. */
      this.closePicker();

      this.render(data);
      /* `/cart/add.js` answers with the line that was added, not the
         whole cart, so the count is read back off the markup it just
         returned rather than guessed at. */
      const chip = this.panel.querySelector('.cnoma__count');
      this.setCount(chip ? Number(chip.textContent.trim()) : null);
    } catch (error) {
      console.error('Cart drawer could not add the item:', error);
      this.showError('Something went wrong. Please try again.');
    } finally {
      this.busy = false;
      this.panel.classList.remove('is-pending');
      if (button) {
        button.classList.remove('is-busy');
        button.disabled = false;
      }
      /* Same as the form path: the strip can only load once the pending
         flag is off. */
      if (this.open) this.loadCrossSell();
    }
  }

  /* Pull the swap region out of the returned section HTML and put it in
   * place of the current one. Parsed with DOMParser rather than assigned
   * as innerHTML on a live node, so a half-written string is never on
   * screen. */
  render(data) {
    const html = data && data.sections && data.sections['cart-noma'];
    if (!html || !this.swap) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = parsed.querySelector('[data-cnoma-swap]');
    if (!next) return;

    this.swap.replaceWith(next);
    this.swap = next;

    /* The swap brought a fresh, empty cross-sell slot with it. */
    if (this.open) this.loadCrossSell();

    const heading = parsed.querySelector('.cnoma__title');
    const currentHeading = this.panel.querySelector('.cnoma__title');
    if (heading && currentHeading) currentHeading.innerHTML = heading.innerHTML;
  }

  /* The variant picker -------------------------------------------------
   *
   * Shopify renders it from the product, at
   * `/products/<handle>?section_id=cart-variant-picker`, so prices are
   * formatted for the buyer's market and availability is Liquid's answer
   * rather than something reconstructed in JavaScript.
   *
   * Fetched on the tap and cached for the rest of the visit -- someone
   * comparing two sizes should pay for it once. */
  async openPicker(handle, button) {
    if (!handle || !this.sheet) return;

    if (button) button.classList.add('is-busy');

    try {
      let html = this.pickerCache.get(handle);

      if (html === undefined) {
        const response = await fetch(`/products/${encodeURIComponent(handle)}?section_id=cart-variant-picker`);
        html = response.ok ? await response.text() : '';
        this.pickerCache.set(handle, html);
      }

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const picker = parsed.querySelector('[data-cnoma-picker]');

      /* No picker means no product to choose from -- unpublished, or the
         handle moved. Sending them to the page is better than a sheet
         that opens empty. */
      if (!picker) {
        window.location.href = `/products/${handle}`;
        return;
      }

      this.sheet.replaceChildren(picker);
      this.sheet.hidden = false;
      /* One frame, so the transition has a closed state to run from. */
      requestAnimationFrame(() => this.sheet.classList.add('is-open'));

      this.pickerOpen = true;
      this.pickerReturn = button || null;
      /* The sheet is the dialog now; focus moves into it so Tab stays
         where the eye is. */
      const first = this.sheet.querySelector('button:not([disabled])');
      if (first) first.focus({ preventScroll: true });
    } catch (error) {
      console.error('Cart drawer could not open the variant picker:', error);
    } finally {
      if (button) button.classList.remove('is-busy');
    }
  }

  closePicker() {
    if (!this.sheet || !this.pickerOpen) return;

    this.pickerOpen = false;
    this.sheet.classList.remove('is-open');

    const done = () => {
      if (!this.pickerOpen) {
        this.sheet.hidden = true;
        this.sheet.replaceChildren();
      }
    };
    /* Emptied only once it has slid away, so it does not vanish mid
       transition. */
    window.setTimeout(done, 280);

    if (this.pickerReturn && this.panel.contains(this.pickerReturn)) {
      this.pickerReturn.focus({ preventScroll: true });
    }
    this.pickerReturn = null;
  }

  /* Complementary products, fetched the first time the drawer is opened
   * with something in it.
   *
   * Deliberately not rendered with the page. `/recommendations/products`
   * is a live call, and paying for it on every page load to fill a panel
   * most visitors never open is the kind of cost that put Rebuy at 1.7
   * seconds. This costs nothing until the cart is actually looked at, and
   * the result is cached per product for the rest of the visit.
   *
   * `intent=complementary` returns what Search & Discovery says goes with
   * the product. If nothing has been set there yet, one fallback call
   * asks for related products instead -- an empty strip is worse than a
   * looser match, and the fallback disappears on its own as soon as the
   * complementary lists are filled in. */
  async loadCrossSell() {
    /* Not while an add is in flight: the swap that lands in a moment
       brings a fresh slot with the new first item's id, and fetching for
       the old one first would be a wasted request answered too late to
       use. */
    if (this.panel.classList.contains('is-pending')) return;

    const slot = this.panel.querySelector('[data-cnoma-cross]');
    if (!slot || slot.dataset.loaded) return;

    const productId = slot.getAttribute('data-product-id');
    if (!productId) return;

    slot.dataset.loaded = '1';
    this.crossCache = this.crossCache || new Map();

    const cached = this.crossCache.get(productId);
    if (cached !== undefined) {
      this.fillCrossSell(slot, cached);
      return;
    }

    const ask = async (intent) => {
      const url = `${window.routes.product_recommendations_url || '/recommendations/products'}` +
        `?section_id=cart-cross-sell&product_id=${encodeURIComponent(productId)}&limit=4&intent=${intent}`;
      const response = await fetch(url);
      if (!response.ok) return '';
      return (await response.text()).trim();
    };

    try {
      let html = await ask('complementary');
      if (!this.hasProducts(html)) html = await ask('related');
      this.crossCache.set(productId, html);
      this.fillCrossSell(slot, html);
    } catch (error) {
      console.error('Cart drawer could not load recommendations:', error);
    }
  }

  /* The endpoint answers 200 with a section wrapper even when it found
   * nothing, so "did it work" is a question about the markup, not the
   * status code. */
  hasProducts(html) {
    return !!html && html.indexOf('cnoma__crossitem') !== -1;
  }

  fillCrossSell(slot, html) {
    if (!this.hasProducts(html)) {
      slot.hidden = true;
      return;
    }

    /* Shopify returns the section inside a wrapper div carrying the
       section id. Taking the heading and the list by name skips that
       wrapper without depending on what it is called. */
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const heading = parsed.querySelector('.cnoma__crosshead');
    const list = parsed.querySelector('.cnoma__crosslist');
    if (!list) {
      slot.hidden = true;
      return;
    }

    slot.replaceChildren();
    if (heading) slot.appendChild(heading);
    slot.appendChild(list);
    slot.hidden = false;
  }

  /* Keep the header bubble honest. It lives outside the drawer, so the
   * section swap does not reach it. */
  setCount(count) {
    const icon = document.getElementById('cart-icon-bubble');
    if (!icon) return;

    let bubble = icon.querySelector('.cart-count-bubble');

    if (!count) {
      if (bubble) bubble.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      icon.appendChild(bubble);
    }

    /* Both spans, the way the Liquid writes it.
       Rebuilding with only the `aria-hidden` one left the bubble
       announcing nothing at all after any cart interaction -- the visible
       number was there and a screen reader heard silence. */
    const strings = window.cartStrings || {};
    let spoken;
    if (count === 1 && strings.itemCountOne) {
      spoken = strings.itemCountOne;
    } else if (strings.itemCount) {
      spoken = strings.itemCount.replace('[count]', count);
    } else {
      spoken = `${count} ${count === 1 ? 'item' : 'items'}`;
    }

    bubble.replaceChildren();

    const shown = document.createElement('span');
    shown.setAttribute('aria-hidden', 'true');
    shown.textContent = count < 100 ? String(count) : '';
    bubble.appendChild(shown);

    const hidden = document.createElement('span');
    hidden.className = 'visually-hidden';
    hidden.textContent = spoken;
    bubble.appendChild(hidden);
  }

  lock() {
    this.prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }

  unlock() {
    document.documentElement.style.overflow = this.prevOverflow || '';
  }
}

if (!customElements.get('cart-noma')) {
  customElements.define('cart-noma', CartNoma);
}
